import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Group,
  Header,
  PersonalText,
  SubmitBtn,
  NotificationDiv,
  NotificationContent,
  ProjectHeadingTop,
  Title1,
  NotificationForm,
  Container
} from '../../styles';
import Button from '../../../../component/button';
import { getNotificationSettingDetails, updateNotificationSettingDetails } from '../../../../services/settingServices';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { BUTTON_TYPE } from '../../../../global/constants';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../../../mobx/rootStore';
import CheckboxInput from '../../../../component/input/checkboxInput';

const Notifications: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  //MobX state variables
  const { settingStore } = rootStore;
  const { notificationSettingDetails, clearNotificationSettingDetails } = settingStore;
  // Other variables
  const isMobile = useMobileDevice();

  // Loads notification settings data from the server
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await getNotificationSettingDetails();
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => {
      clearNotificationSettingDetails();
    };
  }, [clearNotificationSettingDetails, loadData]);

  // Updates notification settings in the state
  const onUpdateNotificationDetails = useCallback(
    (propertyName: 'Browser' | 'Email' | 'Mobile', value: boolean) => {
      settingStore.setNotificationProperty(propertyName, value);
    },
    [settingStore]
  );

  // Handles form submission and saves notification settings
  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        const { updated, ...payload } = notificationSettingDetails || {};
        await updateNotificationSettingDetails(payload);
        settingStore.setNotificationProperty('updated', false);
      } catch (error) {
        console.log('error', error);
      } finally {
        setLoading(false);
      }
    },
    [notificationSettingDetails, settingStore]
  );

  const renderUpdateButton = useMemo(() => {
    return <Button title='Update' variant={BUTTON_TYPE.PRIMARY} buttonType='submit' isLoading={loading} size='small' />;
  }, [loading]);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form' onSubmit={onSubmit}>
            {!isMobile ? (
              <Header>
                <PersonalText>Notifications</PersonalText>
                <SubmitBtn>{renderUpdateButton}</SubmitBtn>
              </Header>
            ) : (
              <ResponsiveHeader title='Notifications' />
            )}
            <NotificationForm>
              <NotificationDiv>
                <NotificationContent>
                  <ProjectHeadingTop
                    role='button'
                    onClick={() => onUpdateNotificationDetails('Browser', !notificationSettingDetails?.Browser)}>
                    <CheckboxInput
                      checked={notificationSettingDetails?.Browser}
                      disabled={loading}
                      onChange={() => onUpdateNotificationDetails('Browser', !notificationSettingDetails?.Browser)}
                    />
                    <Title1>Browser notification</Title1>
                  </ProjectHeadingTop>
                </NotificationContent>
                <NotificationContent>
                  <ProjectHeadingTop
                    role='button'
                    onClick={() => onUpdateNotificationDetails('Email', !notificationSettingDetails?.Email)}>
                    <CheckboxInput
                      checked={notificationSettingDetails?.Email}
                      onChange={() => onUpdateNotificationDetails('Email', !notificationSettingDetails?.Email)}
                      disabled={loading}
                    />
                    <Title1>Email notification</Title1>
                  </ProjectHeadingTop>
                </NotificationContent>
                <NotificationContent>
                  <ProjectHeadingTop
                    role='button'
                    onClick={() => onUpdateNotificationDetails('Mobile', !notificationSettingDetails?.Mobile)}>
                    <CheckboxInput
                      checked={notificationSettingDetails?.Mobile}
                      onChange={() => onUpdateNotificationDetails('Mobile', !notificationSettingDetails?.Mobile)}
                      disabled={loading}
                    />
                    <Title1>Mobile push notification</Title1>
                  </ProjectHeadingTop>
                </NotificationContent>
              </NotificationDiv>
              {isMobile && renderUpdateButton}
            </NotificationForm>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};
export default observer(Notifications);
